import { ChevronRight } from 'lucide-react'
import { Fragment, useState } from 'react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { Cap } from '../../primitives'

export const DESKTOP_WIDTH = 1100
export const MOBILE_WIDTH = 375

export interface Pane {
  label: string
  width: number
  content: React.ReactNode
  /** Opens a wider gap before this pane, to read as the start of a group. */
  startsGroup?: boolean
  /** Drops the pixel width from the label, for a pane that is not a screen. */
  hideWidth?: boolean
  /** A rule between groups rather than a pane: no label, no content. */
  divider?: boolean
  /** Panes sharing a name sit under one heading, as one argument. */
  group?: string
}

interface OptionSectionProps {
  /** Carries the option's number too — there is no second line to put it on. */
  title: string
  note?: React.ReactNode
  /** A prototype control for this option — sits beside the title, not in the views. */
  action?: React.ReactNode
  /** Open from the start. The rest stay shut so the page is a list you can scan. */
  defaultOpen?: boolean
  panes: Pane[]
}

/** Consecutive panes sharing a group, so a heading can span the lot of them. */
function runs(panes: Pane[]): { group?: string; panes: Pane[] }[] {
  const out: { group?: string; panes: Pane[] }[] = []
  for (const pane of panes) {
    const open = out.at(-1)
    if (open && open.group === pane.group) open.panes.push(pane)
    else out.push({ group: pane.group, panes: [pane] })
  }
  return out
}

function renderPane(pane: Pane, index: number) {
  return pane.divider ? (
    <div key={index} className="border-ink-300 shrink-0 self-stretch border-l" />
  ) : (
    <div
      key={index}
      className={cn('flex shrink-0 flex-col gap-2', pane.startsGroup && 'ml-10')}
      style={{ width: pane.width }}
    >
      <Cap>{pane.hideWidth ? pane.label : `${pane.label} · ${pane.width}px`}</Cap>
      {pane.content}
    </div>
  )
}

/**
 * One placement, drawn across its views side by side. Collapsed to a row until
 * asked for: five options at full size is a page nobody can see the shape of,
 * and the panes only mount once opened.
 */
export function OptionSection({ title, note, action, defaultOpen, panes }: OptionSectionProps) {
  const [open, setOpen] = useState(defaultOpen ?? false)

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen}>
      <section className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <CollapsibleTrigger className="group flex flex-1 cursor-pointer items-start gap-2 text-left">
            <ChevronRight
              className={cn('text-ink-400 mt-1.5 size-4 shrink-0 transition-transform', open && 'rotate-90')}
            />
            <span className="flex flex-col gap-1">
              <h2 className="text-ink group-hover:text-ink-600 text-2xl font-extrabold tracking-tight">
                {title}
              </h2>
              {note && <p className="text-ink-500 max-w-3xl text-sm leading-relaxed">{note}</p>}
            </span>
          </CollapsibleTrigger>
          {/* Outside the trigger: a button inside a button is invalid, and the
              control means nothing while the panes are shut. */}
          {open && action}
        </div>

        <CollapsibleContent>
          {panes.length === 0 && (
            <div className="text-ink-400 border-ink-100 flex h-32 items-center justify-center rounded-sm border border-dashed text-xs">
              Not drawn yet.
            </div>
          )}
          <div className="flex items-start gap-6 overflow-x-auto pb-2">
            {runs(panes).map((run, index) =>
              run.group ? (
                <div key={index} className="flex shrink-0 flex-col gap-3">
                  <div className="flex flex-col gap-2">
                    <h3 className="text-ink text-lg font-bold tracking-tight">{run.group}</h3>
                    <div className="border-ink-300 border-t" />
                  </div>
                  <div className="flex items-start gap-6">{run.panes.map(renderPane)}</div>
                </div>
              ) : (
                <Fragment key={index}>{run.panes.map(renderPane)}</Fragment>
              )
            )}
          </div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  )
}
